import { memo } from 'react';

import { NumberField } from '@myn/mui';

import {
  BUILDER_ROW_TYPES,
  DECIMAL_LIMIT,
} from '../../../../../../constants/campaignBuilder.js';
import { BuilderTableColumn, BuilderTableRow } from '../../../../types.js';
import FormattedField from '../FormattedField/FormattedField.jsx';
import useEditableNumericField from '../hooks/useEditableNumericField.js';
import TableEditCell from '../TableEditCell/TableEditCell.jsx';
import InputWrapper from '../wrappers/FormattingInputWrapper.jsx';

type Props = {
  value: string | null;
  column: BuilderTableColumn;
  row: BuilderTableRow;
  cellColor: string;
};

const NumberFieldEditable = memo(({ value, column, row, cellColor }: Props) => {
  const decimalLimit = column.decimals ?? DECIMAL_LIMIT.INTEGER;
  const { type, scale } = row.original;
  const isTileRow = type === BUILDER_ROW_TYPES.TILE;

  const {
    isFieldFocused,
    handleFieldFocus,
    handleFieldBlur,
    isReadOnlyField,
    numberValue,
    handleValueChange,
    handleValueReset,
  } = useEditableNumericField({
    value,
    column,
    row,
  });

  const isResetBtnShown =
    (!!numberValue || numberValue === 0) && !isFieldFocused;
  // hide zero value for every row but the media row level
  const isEmptyValueHidden =
    !Number(value) && !!column.hideEmptyValueForReadOnlyRowLevel && !isTileRow;

  if (isReadOnlyField) {
    return (
      <FormattedField
        scale={scale}
        value={value}
        decimalLimit={decimalLimit}
        isEmptyValueHidden={isEmptyValueHidden}
        color={cellColor}
        variant="number"
      />
    );
  }

  if (!isFieldFocused) {
    return (
      <TableEditCell
        scale={scale}
        isDeleteButtonShown={isResetBtnShown}
        deleteValueFunc={handleValueReset}
        showEditModeFunc={handleFieldFocus}
        startMeasurePerformance={column.startMeasurePerformance}
        value={numberValue}
        decimalLimit={decimalLimit}
        variant="number"
      />
    );
  }

  return (
    <InputWrapper $scale={scale}>
      <NumberField
        value={numberValue}
        onBlur={handleFieldBlur}
        onChange={handleValueChange}
        autoFocus
        maxDecimals={decimalLimit}
        restFormatProps={{
          min: 0,
          minDecimals: decimalLimit,
        }}
      />
    </InputWrapper>
  );
});

export default NumberFieldEditable;
